import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Card,
  CardContent,
  Divider,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  Collapse,
  InputAdornment,
} from '@mui/material';
import {
  Delete,
  Edit,
  Add,
  ListAlt,
  ArrowUpward,
  ArrowDownward,
  Visibility,
  UploadFile,
  IosShare as ShareIcon,
  FileDownload as DownloadIcon,
  ExpandMore,
  ContentPaste,
  AutoFixHigh,
  Close,
  ContentCopy as DuplicateIcon,
  EditNote as EditItemsIcon,
  LockOutlined as LockIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  LibraryAdd,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import useStore from '../store/useStore';
import { translations } from '../translations';

const STEPS_EN = [
  'Name & Description',
  'Add Themes',
  'Add Items',
  'Review & Publish',
];
const STEPS_RU = ['Название', 'Добавить темы', 'Добавить элементы', 'Проверка'];

// --- Bulk Input Parser ---
function parseBulkInput(text) {
  if (!text.trim()) return [];
  return text
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

// --- Theme Editor Component ---
function ThemeEditor({
  theme,
  themeIndex,
  onUpdate,
  onDelete,
  onMove,
  isFirst,
  isLast,
  language,
}) {
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkType, setBulkType] = useState('word');
  const [newItemText, setNewItemText] = useState('');
  const [newItemMeaning, setNewItemMeaning] = useState('');
  const [newItemType, setNewItemType] = useState('word');
  const [editingName, setEditingName] = useState(false);
  const [tempName, setTempName] = useState(theme.name);

  const handleAddItem = () => {
    if (!newItemText.trim()) return;
    const updated = {
      ...theme,
      items: [
        ...theme.items,
        {
          text: newItemText.trim(),
          type: newItemType,
          meaning: newItemMeaning.trim(),
        },
      ],
    };
    onUpdate(themeIndex, updated);
    setNewItemText('');
    setNewItemMeaning('');
  };

  const handleBulkAdd = () => {
    const parsed = parseBulkInput(bulkText);
    if (parsed.length === 0) return;
    const existingTexts = new Set(theme.items.map((i) => i.text.toLowerCase()));
    const newItems = parsed
      .filter((text) => !existingTexts.has(text.toLowerCase()))
      .map((text) => ({ text, type: bulkType }));
    const duplicates = parsed.length - newItems.length;
    const updated = { ...theme, items: [...theme.items, ...newItems] };
    onUpdate(themeIndex, updated);
    setBulkText('');
    setBulkMode(false);
    if (duplicates > 0) {
      alert(
        language === 'en'
          ? `Added ${newItems.length} items. Skipped ${duplicates} duplicates.`
          : `Добавлено ${newItems.length}. Пропущено дубликатов: ${duplicates}.`
      );
    }
  };

  const handleRemoveItem = (idx) => {
    const updated = {
      ...theme,
      items: theme.items.filter((_, i) => i !== idx),
    };
    onUpdate(themeIndex, updated);
  };

  const handleNameSave = () => {
    if (tempName.trim()) {
      onUpdate(themeIndex, { ...theme, name: tempName.trim() });
    }
    setEditingName(false);
  };

  const words = theme.items.filter((i) => i.type === 'word');
  const phrases = theme.items.filter((i) => i.type === 'phrase');

  return (
    <Accordion
      defaultExpanded={theme.items.length === 0}
      sx={{
        borderRadius: '12px !important',
        mb: 1,
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 },
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flex: 1, mr: 1 }}
        >
          {editingName ? (
            <TextField
              size="small"
              value={tempName}
              onChange={(e) => setTempName(e.target.value)}
              onBlur={handleNameSave}
              onKeyDown={(e) => e.key === 'Enter' && handleNameSave()}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              sx={{ minWidth: 200 }}
            />
          ) : (
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                setEditingName(true);
                setTempName(theme.name);
              }}
            >
              {theme.name || 'Untitled Theme'}
            </Typography>
          )}
          <Chip
            label={`${words.length} ${language === 'en' ? 'words' : 'слов'}`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`${phrases.length} ${language === 'en' ? 'phrases' : 'фраз'}`}
            size="small"
            color="secondary"
            variant="outlined"
          />
        </Stack>
        <Stack
          direction="row"
          spacing={0.5}
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            size="small"
            onClick={() => onMove(themeIndex, -1)}
            disabled={isFirst}
          >
            <ArrowUpward fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onMove(themeIndex, 1)}
            disabled={isLast}
          >
            <ArrowDownward fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => onDelete(themeIndex)}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {theme.description !== undefined && (
          <TextField
            fullWidth
            size="small"
            label={language === 'en' ? 'Theme Description' : 'Описание темы'}
            value={theme.description || ''}
            onChange={(e) =>
              onUpdate(themeIndex, { ...theme, description: e.target.value })
            }
            sx={{ mb: 2 }}
          />
        )}

        {/* Item input */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
          {!bulkMode ? (
            <>
              <TextField
                size="small"
                fullWidth
                label={language === 'en' ? 'Add item' : 'Добавить элемент'}
                value={newItemText}
                onChange={(e) => setNewItemText(e.target.value)}
                onKeyDown={(e) =>
                  e.key === 'Enter' && (e.preventDefault(), handleAddItem())
                }
              />
              <TextField
                size="small"
                label={
                  language === 'en'
                    ? 'Meaning (optional)'
                    : 'Значение (необязательно)'
                }
                placeholder={
                  language === 'en' ? 'animal / food' : 'животное / еда'
                }
                value={newItemMeaning}
                onChange={(e) => setNewItemMeaning(e.target.value)}
                onKeyDown={(e) =>
                  e.key === 'Enter' && (e.preventDefault(), handleAddItem())
                }
                sx={{ width: 200, flexShrink: 0 }}
              />
              <ToggleButtonGroup
                size="small"
                value={newItemType}
                exclusive
                onChange={(e, v) => v && setNewItemType(v)}
              >
                <ToggleButton value="word" sx={{ px: 1.5, fontSize: '0.7rem' }}>
                  W
                </ToggleButton>
                <ToggleButton
                  value="phrase"
                  sx={{ px: 1.5, fontSize: '0.7rem' }}
                >
                  P
                </ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="contained"
                size="small"
                onClick={handleAddItem}
                sx={{ minWidth: 40 }}
              >
                <Add />
              </Button>
              <Tooltip
                title={language === 'en' ? 'Bulk paste' : 'Вставить список'}
              >
                <IconButton
                  size="small"
                  onClick={() => setBulkMode(true)}
                  color="secondary"
                >
                  <ContentPaste />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {language === 'en' ? 'Bulk Add' : 'Массовое добавление'}
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  value={bulkType}
                  exclusive
                  onChange={(e, v) => v && setBulkType(v)}
                >
                  <ToggleButton
                    value="word"
                    sx={{ px: 1.5, fontSize: '0.7rem' }}
                  >
                    {language === 'en' ? 'Words' : 'Слова'}
                  </ToggleButton>
                  <ToggleButton
                    value="phrase"
                    sx={{ px: 1.5, fontSize: '0.7rem' }}
                  >
                    {language === 'en' ? 'Phrases' : 'Фразы'}
                  </ToggleButton>
                </ToggleButtonGroup>
                <IconButton size="small" onClick={() => setBulkMode(false)}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>
              <TextField
                multiline
                rows={4}
                fullWidth
                size="small"
                placeholder={
                  language === 'en'
                    ? 'Paste words/phrases here, one per line or comma-separated:\napple, banana, cherry\ndog\ncat'
                    : 'Вставьте слова/фразы, по одному в строке или через запятую'
                }
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
              />
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}
              >
                <Typography variant="caption" color="text.secondary">
                  {parseBulkInput(bulkText).length}{' '}
                  {language === 'en' ? 'items detected' : 'элементов'}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleBulkAdd}
                  disabled={parseBulkInput(bulkText).length === 0}
                >
                  {language === 'en'
                    ? `Add ${parseBulkInput(bulkText).length} Items`
                    : `Добавить ${parseBulkInput(bulkText).length}`}
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        {/* Items display */}
        {theme.items.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {theme.items.map((item, idx) => (
              <Chip
                key={idx}
                label={
                  item.meaning ? `${item.text} (${item.meaning})` : item.text
                }
                onDelete={() => handleRemoveItem(idx)}
                color={item.type === 'phrase' ? 'secondary' : 'primary'}
                variant="outlined"
                size="small"
                sx={{ maxWidth: 250 }}
              />
            ))}
          </Box>
        )}
        {theme.items.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', py: 2 }}
          >
            {language === 'en'
              ? 'No items yet. Add words or phrases above.'
              : 'Пока нет элементов. Добавьте слова или фразы.'}
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

// --- Safe-edit: inline add-item row (owns its own draft state) ---
function SafeAddItemRow({ themeIdx, onAdd, language }) {
  const [text, setText] = useState('');
  const [meaning, setMeaning] = useState('');
  const [type, setType] = useState('word');
  const handleAdd = () => {
    if (!text.trim()) return;
    onAdd(themeIdx, {
      text: text.trim(),
      type,
      meaning: meaning.trim(),
      _isNew: true,
    });
    setText('');
    setMeaning('');
  };
  return (
    <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center' }}>
      <TextField
        size="small"
        fullWidth
        placeholder={language === 'en' ? 'Add new item…' : 'Добавить элемент…'}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) =>
          e.key === 'Enter' && (e.preventDefault(), handleAdd())
        }
      />
      <TextField
        size="small"
        placeholder={
          language === 'en'
            ? 'animal / food, optional'
            : 'животное / еда, необязательно'
        }
        value={meaning}
        onChange={(e) => setMeaning(e.target.value)}
        onKeyDown={(e) =>
          e.key === 'Enter' && (e.preventDefault(), handleAdd())
        }
        sx={{ width: 180, flexShrink: 0 }}
      />
      <ToggleButtonGroup
        size="small"
        value={type}
        exclusive
        onChange={(_, v) => v && setType(v)}
      >
        <ToggleButton value="word" sx={{ px: 1.5, fontSize: '0.7rem' }}>
          W
        </ToggleButton>
        <ToggleButton value="phrase" sx={{ px: 1.5, fontSize: '0.7rem' }}>
          P
        </ToggleButton>
      </ToggleButtonGroup>
      <Button
        variant="contained"
        size="small"
        onClick={handleAdd}
        sx={{ minWidth: 40 }}
      >
        <Add />
      </Button>
    </Box>
  );
}

export default function TemplatePage() {
  const [templates, setTemplates] = useState([]);
  const [importing, setImporting] = useState(false);
  const [jsonImporting, setJsonImporting] = useState(false);

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateTags, setTemplateTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [themes, setThemes] = useState([]);
  const [newThemeName, setNewThemeName] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  // Safe-edit state
  const [safeEditOpen, setSafeEditOpen] = useState(false);
  const [safeEditTemplate, setSafeEditTemplate] = useState(null);
  const [safeEditThemes, setSafeEditThemes] = useState([]);
  const [safeEditSaving, setSafeEditSaving] = useState(false);
  const [newSafeThemeName, setNewSafeThemeName] = useState('');

  // List UX state
  const [builderOpen, setBuilderOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTagFilter, setActiveTagFilter] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuTemplate, setMenuTemplate] = useState(null);
  const [importMenuAnchor, setImportMenuAnchor] = useState(null);

  const { language } = useStore();
  const t = translations[language];
  const steps = language === 'en' ? STEPS_EN : STEPS_RU;

  // Auto-save draft to localStorage
  useEffect(() => {
    const draft = {
      templateName,
      templateDescription,
      templateTags,
      themes,
      editingId: editingTemplate?._id,
    };
    localStorage.setItem('templateDraft', JSON.stringify(draft));
  }, [
    templateName,
    templateDescription,
    templateTags,
    themes,
    editingTemplate,
  ]);

  // Restore draft on mount
  useEffect(() => {
    const saved = localStorage.getItem('templateDraft');
    if (saved) {
      try {
        const draft = JSON.parse(saved);
        if (
          draft.templateName &&
          draft.themes?.length > 0 &&
          !editingTemplate
        ) {
          const restore = window.confirm(
            language === 'en'
              ? 'You have an unsaved template draft. Restore it?'
              : 'У вас есть несохраненный черновик. Восстановить?'
          );
          if (restore) {
            setTemplateName(draft.templateName);
            setTemplateDescription(draft.templateDescription || '');
            setTemplateTags(draft.templateTags || []);
            setThemes(draft.themes || []);
            setActiveStep(draft.themes?.length > 0 ? 2 : 0);
          } else {
            localStorage.removeItem('templateDraft');
          }
        }
      } catch (e) {
        /* ignore */
      }
    }
  }, []);

  useEffect(() => {
    fetch('/api/templates', { credentials: 'include' })
      .then((res) => res.json())
      .then(setTemplates);
  }, []);

  const handleAddTheme = () => {
    if (!newThemeName.trim()) return;
    setThemes([
      ...themes,
      { name: newThemeName.trim(), description: '', items: [] },
    ]);
    setNewThemeName('');
  };

  const handleUpdateTheme = (idx, updated) => {
    const newThemes = [...themes];
    newThemes[idx] = updated;
    setThemes(newThemes);
  };

  const handleDeleteTheme = (idx) => {
    setThemes(themes.filter((_, i) => i !== idx));
  };

  const handleMoveTheme = (idx, direction) => {
    const newThemes = [...themes];
    const target = idx + direction;
    if (target < 0 || target >= themes.length) return;
    [newThemes[idx], newThemes[target]] = [newThemes[target], newThemes[idx]];
    setThemes(newThemes);
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !templateTags.includes(tagInput.trim())) {
      setTemplateTags([...templateTags, tagInput.trim()]);
      setTagInput('');
    }
  };

  async function handleSubmit() {
    if (!templateName || themes.length === 0) return;

    const url = editingTemplate
      ? `/api/templates/${editingTemplate._id}`
      : '/api/templates';
    const method = editingTemplate ? 'PATCH' : 'POST';

    const res = await fetch(url, {
      method,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: templateName,
        description: templateDescription,
        themes,
        tags: templateTags,
      }),
    });

    if (res.ok) {
      const updatedTemplate = await res.json();
      if (editingTemplate) {
        setTemplates(
          templates.map((t) =>
            t._id === editingTemplate._id ? updatedTemplate : t
          )
        );
      } else {
        setTemplates([...templates, updatedTemplate]);
      }
      resetForm();
    }
  }

  function resetForm() {
    setTemplateName('');
    setTemplateDescription('');
    setTemplateTags([]);
    setThemes([]);
    setEditingTemplate(null);
    setActiveStep(0);
    setBuilderOpen(false);
    localStorage.removeItem('templateDraft');
  }

  function handleEdit(template) {
    setEditingTemplate(template);
    setTemplateName(template.name);
    setTemplateDescription(template.description || '');
    setTemplateTags(template.tags || []);
    setThemes(
      template.themes.map((t) => ({
        ...t,
        name: t.name || t.themeName || '',
        items: t.items || [],
      }))
    );
    setActiveStep(2); // Jump to items step
    setBuilderOpen(true);
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (_) {}
  }

  function handleStartNew() {
    resetForm();
    setBuilderOpen(true);
  }

  function openCardMenu(e, template) {
    setMenuAnchor(e.currentTarget);
    setMenuTemplate(template);
  }
  function closeCardMenu() {
    setMenuAnchor(null);
    setMenuTemplate(null);
  }

  async function handleClone(id) {
    try {
      const res = await fetch(`/api/templates/${id}/clone`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const cloned = await res.json();
        setTemplates((prev) => [cloned, ...prev]);
      }
    } catch {
      // silently fail — user will see nothing happened
    }
  }

  // ── Safe-edit handlers ──────────────────────────────────────
  function handleOpenSafeEdit(template) {
    setSafeEditThemes(
      template.themes.map((theme) => ({
        ...theme,
        _isNew: false,
        items: (theme.items || []).map((item) => ({ ...item, _isNew: false })),
      }))
    );
    setSafeEditTemplate(template);
    setNewSafeThemeName('');
    setSafeEditOpen(true);
  }

  function handleSafeEditAddItem(themeIdx, newItem) {
    setSafeEditThemes((prev) => {
      const updated = [...prev];
      updated[themeIdx] = {
        ...updated[themeIdx],
        items: [...updated[themeIdx].items, newItem],
      };
      return updated;
    });
  }

  function handleSafeEditRemoveItem(themeIdx, itemIdx) {
    setSafeEditThemes((prev) => {
      const updated = [...prev];
      updated[themeIdx] = {
        ...updated[themeIdx],
        items: updated[themeIdx].items.filter((_, i) => i !== itemIdx),
      };
      return updated;
    });
  }

  function handleSafeEditItemText(themeIdx, itemIdx, text) {
    setSafeEditThemes((prev) => {
      const updated = [...prev];
      const items = [...updated[themeIdx].items];
      items[itemIdx] = { ...items[itemIdx], text };
      updated[themeIdx] = { ...updated[themeIdx], items };
      return updated;
    });
  }

  function handleSafeEditItemMeaning(themeIdx, itemIdx, meaning) {
    setSafeEditThemes((prev) => {
      const updated = [...prev];
      const items = [...updated[themeIdx].items];
      items[itemIdx] = { ...items[itemIdx], meaning };
      updated[themeIdx] = { ...updated[themeIdx], items };
      return updated;
    });
  }

  function handleSafeEditThemeName(themeIdx, name) {
    setSafeEditThemes((prev) => {
      const updated = [...prev];
      updated[themeIdx] = { ...updated[themeIdx], name };
      return updated;
    });
  }

  function handleSafeEditAddTheme() {
    if (!newSafeThemeName.trim()) return;
    setSafeEditThemes((prev) => [
      ...prev,
      {
        name: newSafeThemeName.trim(),
        description: '',
        items: [],
        _isNew: true,
      },
    ]);
    setNewSafeThemeName('');
  }

  async function handleSafeDeleteTheme(themeIdx) {
    const theme = safeEditThemes[themeIdx];
    const themeName = theme.name;
    const themeId = theme._id?.toString();

    // New (unsaved) theme — just remove from local state, no server call needed
    if (theme._isNew) {
      setSafeEditThemes((prev) => prev.filter((_, i) => i !== themeIdx));
      return;
    }

    // Confirm with native dialog (avoids nested MUI dialog z-index issues)
    const confirmed = window.confirm(
      language === 'en'
        ? `Delete theme "${themeName}" from template?\n\nExisting student profiles won't be changed automatically. An "Out of sync" button will appear on each affected profile so teachers can review and remove the theme selectively.`
        : `Удалить тему «${themeName}» из шаблона?\n\nСуществующие профили учеников не изменятся автоматически. Кнопка «Не синхронизировано» появится в каждом затронутом профиле для ручного удаления.`
    );
    if (!confirmed) return;

    // Existing theme — cascade delete on server
    try {
      console.log(
        '[deleteTheme] templateId:',
        safeEditTemplate._id,
        'themeId:',
        themeId,
        'themeName:',
        themeName
      );
      const res = await fetch(
        `/api/templates/${safeEditTemplate._id}/themes/${themeId}`,
        { method: 'DELETE', credentials: 'include' }
      );
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        const { template: updatedTemplate, orphanedProfileCount } = body;
        setTemplates((prev) =>
          prev.map((t) => (t._id === updatedTemplate._id ? updatedTemplate : t))
        );
        // Rebuild dialog state from server response so IDs stay in sync
        setSafeEditThemes(
          updatedTemplate.themes.map((t) => ({
            ...t,
            _isNew: false,
            items: (t.items || []).map((item) => ({ ...item, _isNew: false })),
          }))
        );
        setSafeEditTemplate(updatedTemplate);
        alert(
          language === 'en'
            ? orphanedProfileCount > 0
              ? `Theme "${themeName}" removed from template. ${orphanedProfileCount} student profile(s) still have this theme — open each profile to sync selectively.`
              : `Theme "${themeName}" removed from template. No existing profiles were affected.`
            : orphanedProfileCount > 0
              ? `Тема «${themeName}» удалена из шаблона. ${orphanedProfileCount} профиля(ей) ещё содержат эту тему — откройте каждый профиль для синхронизации.`
              : `Тема «${themeName}» удалена из шаблона. Существующие профили не затронуты.`
        );
      } else {
        console.error('[deleteTheme] server error:', res.status, body);
        alert(
          language === 'en'
            ? `Could not delete theme: ${body.message || `HTTP ${res.status}`}`
            : `Не удалось удалить тему: ${body.message || `HTTP ${res.status}`}`
        );
      }
    } catch (err) {
      console.error('[deleteTheme] fetch error:', err);
      alert(
        language === 'en'
          ? `Network error: ${err.message}`
          : `Ошибка сети: ${err.message}`
      );
    }
  }

  async function handleSafeEditSave() {
    setSafeEditSaving(true);
    try {
      // Preserve _id on existing themes/items so Mongoose won't regenerate them
      const cleanThemes = safeEditThemes.map(({ _isNew: _t, ...theme }) => ({
        ...(theme._id ? { _id: theme._id } : {}),
        name: theme.name,
        description: theme.description || '',
        items: theme.items.map(({ _isNew, ...item }) => item),
      }));
      const res = await fetch(`/api/templates/${safeEditTemplate._id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ themes: cleanThemes }),
      });
      if (res.ok) {
        const updated = await res.json();
        setTemplates((prev) =>
          prev.map((t) => (t._id === updated._id ? updated : t))
        );
        setSafeEditOpen(false);
      }
    } finally {
      setSafeEditSaving(false);
    }
  }
  // ────────────────────────────────────────────────────────────

  async function handleDelete(id) {
    if (
      !window.confirm(
        language === 'en' ? 'Delete this template?' : 'Удалить этот шаблон?'
      )
    )
      return;
    await fetch(`/api/templates/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    setTemplates(templates.filter((t) => t._id !== id));
  }

  const handlePreview = (template) => {
    setPreviewTemplate(template);
    setPreviewOpen(true);
  };

  const handleSchemaImport = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setImporting(true);
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    try {
      const res = await fetch('/api/admin/import-schema-template', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        fetch('/api/templates', { credentials: 'include' })
          .then((res) => res.json())
          .then(setTemplates);
        alert(data.message);
      } else {
        alert(data.message || 'Import failed');
      }
    } catch (err) {
      alert('Network error during import');
    } finally {
      setImporting(false);
      e.target.value = null;
    }
  };

  const handleJsonImport = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setJsonImporting(true);
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    try {
      const res = await fetch('/api/admin/templates/import-json', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        setTemplates((prev) => [...prev, ...data]);
        alert(`Successfully imported ${data.length} templates!`);
      }
    } catch (err) {
      alert('Failed to import JSON templates');
    } finally {
      setJsonImporting(false);
      e.target.value = null;
    }
  };

  const handleExport = (id) => {
    window.open(`/api/admin/templates/${id}/export`);
  };

  const handleDeleteAllTemplates = async () => {
    if (
      !window.confirm(
        language === 'en'
          ? 'WARNING: This will permanently delete ALL templates.'
          : 'ВНИМАНИЕ: Это удалит ВСЕ шаблоны.'
      )
    )
      return;
    const confirmation = window.prompt(
      language === 'en'
        ? 'Type "DELETE ALL" to confirm:'
        : 'Напишите "DELETE ALL" для подтверждения:'
    );
    if (confirmation !== 'DELETE ALL') return;
    try {
      const res = await fetch('/api/admin/templates/all', {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        setTemplates([]);
        alert(
          language === 'en' ? 'All templates deleted.' : 'Все шаблоны удалены.'
        );
      }
    } catch (err) {
      alert('Failed to delete templates');
    }
  };

  const totalItems = themes.reduce((sum, t) => sum + t.items.length, 0);

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return templateName.trim().length > 0;
      case 1:
        return themes.length > 0;
      case 2:
        return totalItems > 0;
      case 3:
        return true;
      default:
        return false;
    }
  };

  const allTags = Array.from(
    new Set(templates.flatMap((tpl) => tpl.tags || []))
  ).sort();
  const filteredTemplates = templates.filter((tpl) => {
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      const hay = [tpl.name, tpl.description || '', ...(tpl.tags || [])]
        .join(' ')
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (activeTagFilter && !(tpl.tags || []).includes(activeTagFilter))
      return false;
    return true;
  });

  return (
    <Layout title={t.templates}>
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
        {/* Top Toolbar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            mb: 3,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              {t.templates}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {language === 'en'
                ? `${templates.length} template${templates.length === 1 ? '' : 's'}`
                : `${templates.length} ${templates.length === 1 ? 'шаблон' : 'шаблонов'}`}
            </Typography>
          </Box>
          <TextField
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={language === 'en' ? 'Search templates…' : 'Поиск…'}
            sx={{ minWidth: { xs: '100%', sm: 240 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            size="medium"
            startIcon={<UploadFile />}
            onClick={(e) => setImportMenuAnchor(e.currentTarget)}
            disabled={importing || jsonImporting}
          >
            {language === 'en' ? 'Import' : 'Импорт'}
          </Button>
          <Menu
            open={!!importMenuAnchor}
            anchorEl={importMenuAnchor}
            onClose={() => setImportMenuAnchor(null)}
          >
            <MenuItem
              component="label"
              onClick={() => setImportMenuAnchor(null)}
            >
              <ListItemIcon>
                <UploadFile fontSize="small" />
              </ListItemIcon>
              {t.importSchema}
              <input
                accept=".xls,.xlsx"
                hidden
                type="file"
                multiple
                onChange={handleSchemaImport}
              />
            </MenuItem>
            <MenuItem
              component="label"
              onClick={() => setImportMenuAnchor(null)}
            >
              <ListItemIcon>
                <ShareIcon fontSize="small" />
              </ListItemIcon>
              {t.importShared}
              <input
                accept=".json"
                hidden
                type="file"
                multiple
                onChange={handleJsonImport}
              />
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setImportMenuAnchor(null);
                handleDeleteAllTemplates();
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon>
                <Delete fontSize="small" color="error" />
              </ListItemIcon>
              {t.deleteAll}
            </MenuItem>
          </Menu>
          <Button
            variant="contained"
            size="medium"
            startIcon={<LibraryAdd />}
            onClick={handleStartNew}
            sx={{ fontWeight: 700 }}
          >
            {language === 'en' ? 'New Template' : 'Новый шаблон'}
          </Button>
        </Box>

        {/* Tag filter */}
        {allTags.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              gap: 0.75,
              flexWrap: 'wrap',
              mb: 3,
              alignItems: 'center',
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mr: 0.5 }}
            >
              {language === 'en' ? 'Filter:' : 'Фильтр:'}
            </Typography>
            <Chip
              size="small"
              label={language === 'en' ? 'All' : 'Все'}
              color={activeTagFilter ? 'default' : 'primary'}
              variant={activeTagFilter ? 'outlined' : 'filled'}
              onClick={() => setActiveTagFilter(null)}
            />
            {allTags.map((tag) => (
              <Chip
                key={tag}
                size="small"
                label={tag}
                color={activeTagFilter === tag ? 'primary' : 'default'}
                variant={activeTagFilter === tag ? 'filled' : 'outlined'}
                onClick={() =>
                  setActiveTagFilter(activeTagFilter === tag ? null : tag)
                }
              />
            ))}
          </Box>
        )}

        {/* Collapsible Builder */}
        <Collapse in={builderOpen} timeout="auto" unmountOnExit={false}>
          <Box sx={{ mb: 4 }}>
            <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 4 }}>
              <Typography
                variant="h5"
                sx={{
                  mb: 1,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <ListAlt color="primary" />
                {editingTemplate
                  ? language === 'en'
                    ? `Editing: ${editingTemplate.name}`
                    : `Редактирование: ${editingTemplate.name}`
                  : language === 'en'
                    ? 'Template Builder'
                    : 'Конструктор шаблонов'}
              </Typography>

              {editingTemplate && (
                <Button size="small" onClick={resetForm} sx={{ mb: 2 }}>
                  {language === 'en' ? 'Cancel Editing' : 'Отменить'}
                </Button>
              )}

              <Stepper activeStep={activeStep} sx={{ mb: 4 }} alternativeLabel>
                {steps.map((label, i) => (
                  <Step
                    key={i}
                    completed={activeStep > i}
                    onClick={() => setActiveStep(i)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {/* Step 0: Name & Description */}
              {activeStep === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                    fullWidth
                    label={
                      language === 'en' ? 'Template Name' : 'Название шаблона'
                    }
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    variant="filled"
                    sx={{ bgcolor: 'action.hover', borderRadius: 1 }}
                    placeholder={
                      language === 'en'
                        ? 'e.g., Level 1 - Beginner'
                        : 'напр., Уровень 1 - Начальный'
                    }
                  />
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label={
                      language === 'en'
                        ? 'Description (optional)'
                        : 'Описание (необязательно)'
                    }
                    value={templateDescription}
                    onChange={(e) => setTemplateDescription(e.target.value)}
                    placeholder={
                      language === 'en'
                        ? 'What this template covers...'
                        : 'Что охватывает этот шаблон...'
                    }
                  />
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {language === 'en' ? 'Tags' : 'Теги'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        size="small"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === 'Enter' &&
                          (e.preventDefault(), handleAddTag())
                        }
                        placeholder={
                          language === 'en' ? 'Add tag...' : 'Добавить тег...'
                        }
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleAddTag}
                      >
                        <Add />
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {[
                        'Beginner',
                        'Intermediate',
                        'Advanced',
                        'Level 1',
                        'Level 2',
                        'Level 3',
                      ].map(
                        (tag) =>
                          !templateTags.includes(tag) && (
                            <Chip
                              key={tag}
                              label={tag}
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                setTemplateTags([...templateTags, tag])
                              }
                              sx={{
                                cursor: 'pointer',
                                opacity: 0.6,
                                '&:hover': { opacity: 1 },
                              }}
                            />
                          )
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        mt: 1,
                      }}
                    >
                      {templateTags.map((tag, i) => (
                        <Chip
                          key={i}
                          label={tag}
                          size="small"
                          color="primary"
                          onDelete={() =>
                            setTemplateTags(
                              templateTags.filter((_, idx) => idx !== i)
                            )
                          }
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Step 1: Add Themes */}
              {activeStep === 1 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label={language === 'en' ? 'Theme Name' : 'Название темы'}
                      value={newThemeName}
                      onChange={(e) => setNewThemeName(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === 'Enter' &&
                        (e.preventDefault(), handleAddTheme())
                      }
                      placeholder={
                        language === 'en'
                          ? 'e.g., Animals, Colors, Greetings'
                          : 'напр., Животные, Цвета, Приветствия'
                      }
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddTheme}
                      disabled={!newThemeName.trim()}
                    >
                      <Add />
                    </Button>
                  </Box>

                  {themes.length === 0 && (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      {language === 'en'
                        ? 'Start by adding themes. Each theme groups related words and phrases.'
                        : 'Начните с добавления тем. Каждая тема группирует связанные слова и фразы.'}
                    </Alert>
                  )}

                  <List>
                    {themes.map((theme, idx) => (
                      <ListItem key={idx} divider sx={{ borderRadius: 2 }}>
                        <Badge
                          badgeContent={theme.items.length}
                          color="primary"
                          sx={{ mr: 2 }}
                        >
                          <ListAlt color="action" />
                        </Badge>
                        <ListItemText
                          primary={theme.name}
                          secondary={
                            theme.description ||
                            (language === 'en'
                              ? 'No description'
                              : 'Без описания')
                          }
                        />
                        <Stack direction="row" spacing={0.5}>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveTheme(idx, -1)}
                            disabled={idx === 0}
                          >
                            <ArrowUpward fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveTheme(idx, 1)}
                            disabled={idx === themes.length - 1}
                          >
                            <ArrowDownward fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteTheme(idx)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Stack>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Step 2: Add Items */}
              {activeStep === 2 && (
                <Box>
                  {themes.length === 0 ? (
                    <Alert severity="warning">
                      {language === 'en'
                        ? 'Go back and add themes first.'
                        : 'Вернитесь и добавьте темы.'}
                    </Alert>
                  ) : (
                    themes.map((theme, idx) => (
                      <ThemeEditor
                        key={idx}
                        theme={theme}
                        themeIndex={idx}
                        onUpdate={handleUpdateTheme}
                        onDelete={handleDeleteTheme}
                        onMove={handleMoveTheme}
                        isFirst={idx === 0}
                        isLast={idx === themes.length - 1}
                        language={language}
                      />
                    ))
                  )}
                </Box>
              )}

              {/* Step 3: Review & Publish */}
              {activeStep === 3 && (
                <Box>
                  <Paper
                    variant="outlined"
                    sx={{ p: 3, borderRadius: 3, mb: 3 }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {templateName}
                    </Typography>
                    {templateDescription && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {templateDescription}
                      </Typography>
                    )}
                    {templateTags.length > 0 && (
                      <Box
                        sx={{
                          mt: 1,
                          display: 'flex',
                          gap: 0.5,
                          flexWrap: 'wrap',
                        }}
                      >
                        {templateTags.map((tag, i) => (
                          <Chip
                            key={i}
                            label={tag}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                    <Divider sx={{ my: 2 }} />
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {themes.length} {language === 'en' ? 'themes' : 'тем'} |{' '}
                      {totalItems}{' '}
                      {language === 'en' ? 'total items' : 'элементов'}
                    </Typography>
                    {themes.map((theme, idx) => (
                      <Box key={idx} sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                        >
                          {theme.name}
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ ml: 1, color: 'text.secondary' }}
                          >
                            (
                            {
                              theme.items.filter((i) => i.type === 'word')
                                .length
                            }
                            W /{' '}
                            {
                              theme.items.filter((i) => i.type === 'phrase')
                                .length
                            }
                            P)
                          </Typography>
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                            mt: 0.5,
                          }}
                        >
                          {theme.items.map((item, i) => (
                            <Chip
                              key={i}
                              label={item.text}
                              size="small"
                              color={
                                item.type === 'phrase' ? 'secondary' : 'default'
                              }
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Paper>

                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handleSubmit}
                    disabled={
                      !templateName || themes.length === 0 || totalItems === 0
                    }
                    sx={{
                      borderRadius: 3,
                      py: 1.5,
                      fontWeight: 700,
                      fontSize: '1rem',
                    }}
                  >
                    {editingTemplate
                      ? language === 'en'
                        ? 'Save Changes'
                        : 'Сохранить изменения'
                      : language === 'en'
                        ? 'Publish Template'
                        : 'Опубликовать шаблон'}
                  </Button>
                </Box>
              )}

              {/* Navigation */}
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}
              >
                <Button
                  disabled={activeStep === 0}
                  onClick={() => setActiveStep((s) => s - 1)}
                >
                  {language === 'en' ? 'Back' : 'Назад'}
                </Button>
                {activeStep < 3 && (
                  <Button
                    variant="contained"
                    onClick={() => setActiveStep((s) => s + 1)}
                    disabled={!canProceed()}
                  >
                    {language === 'en' ? 'Next' : 'Далее'}
                  </Button>
                )}
              </Box>
            </Paper>
          </Box>
        </Collapse>

        {/* Template Grid */}
        {filteredTemplates.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              py: 6,
              textAlign: 'center',
              borderRadius: 3,
              borderStyle: 'dashed',
            }}
          >
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              {templates.length === 0
                ? language === 'en'
                  ? 'No templates yet.'
                  : 'Пока нет шаблонов.'
                : language === 'en'
                  ? 'No templates match.'
                  : 'Ничего не найдено.'}
            </Typography>
            {templates.length === 0 && (
              <Button
                variant="contained"
                startIcon={<LibraryAdd />}
                onClick={handleStartNew}
                sx={{ mt: 1 }}
              >
                {language === 'en'
                  ? 'Create your first template'
                  : 'Создать первый шаблон'}
              </Button>
            )}
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {filteredTemplates.map((template) => {
              const themeCount = template.themes?.length || 0;
              const itemCount =
                template.themes?.reduce(
                  (s, tm) => s + (tm.items?.length || 0),
                  0
                ) || 0;
              return (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={template._id}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'box-shadow .2s, transform .2s',
                      '&:hover': {
                        boxShadow: '0 6px 22px rgba(0,0,0,0.08)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 700,
                              lineHeight: 1.2,
                              wordBreak: 'break-word',
                            }}
                          >
                            {template.name}
                          </Typography>
                          {template.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                mt: 0.25,
                              }}
                            >
                              {template.description}
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => openCardMenu(e, template)}
                          sx={{ flexShrink: 0 }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      {(template.tags || []).length > 0 && (
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 0.5,
                            flexWrap: 'wrap',
                          }}
                        >
                          {(template.tags || []).slice(0, 3).map((tag, i) => (
                            <Chip
                              key={i}
                              label={tag}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          ))}
                          {(template.tags || []).length > 3 && (
                            <Chip
                              label={`+${(template.tags || []).length - 3}`}
                              size="small"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      )}

                      <Box sx={{ flex: 1 }} />

                      <Stack direction="row" spacing={0.75} sx={{ mt: 1 }}>
                        <Chip
                          label={`${themeCount} ${language === 'en' ? 'themes' : 'тем'}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        <Chip
                          label={`${itemCount} ${language === 'en' ? 'items' : 'эл.'}`}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {language === 'en' ? 'By' : 'От'}:{' '}
                        <b>{template.createdBy?.username || 'System'}</b>
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          fullWidth
                          startIcon={<EditItemsIcon fontSize="small" />}
                          onClick={() => handleOpenSafeEdit(template)}
                          sx={{ fontWeight: 600 }}
                        >
                          {language === 'en' ? 'Edit' : 'Править'}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          fullWidth
                          startIcon={<Visibility fontSize="small" />}
                          onClick={() => handlePreview(template)}
                        >
                          {language === 'en' ? 'Preview' : 'Просмотр'}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Card Kebab Menu */}
        <Menu open={!!menuAnchor} anchorEl={menuAnchor} onClose={closeCardMenu}>
          <MenuItem
            onClick={() => {
              const tpl = menuTemplate;
              closeCardMenu();
              if (tpl) handleEdit(tpl);
            }}
          >
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            {language === 'en' ? 'Edit (wizard)' : 'Редактировать (мастер)'}
          </MenuItem>
          <MenuItem
            onClick={() => {
              const tpl = menuTemplate;
              closeCardMenu();
              if (tpl) handleClone(tpl._id);
            }}
          >
            <ListItemIcon>
              <DuplicateIcon fontSize="small" />
            </ListItemIcon>
            {language === 'en' ? 'Duplicate' : 'Дублировать'}
          </MenuItem>
          <MenuItem
            onClick={() => {
              const tpl = menuTemplate;
              closeCardMenu();
              if (tpl) handleExport(tpl._id);
            }}
          >
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            {language === 'en' ? 'Export JSON' : 'Экспорт JSON'}
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              const tpl = menuTemplate;
              closeCardMenu();
              if (tpl) handleDelete(tpl._id);
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            {language === 'en' ? 'Delete' : 'Удалить'}
          </MenuItem>
        </Menu>

        {/* ── Safe Edit Items Dialog ── */}
        <Dialog
          open={safeEditOpen}
          onClose={() => setSafeEditOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EditItemsIcon color="primary" />
              {language === 'en' ? 'Edit Items' : 'Редактировать элементы'}
              {safeEditTemplate && (
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 400 }}
                >
                  — {safeEditTemplate.name}
                </Typography>
              )}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              {language === 'en'
                ? 'Existing items (🔒) can be renamed but not deleted — this protects student scores. New items you add here can be removed freely.'
                : 'Существующие элементы (🔒) можно переименовать, но не удалить — это защищает оценки учеников. Новые элементы можно удалять.'}
            </Alert>

            {safeEditThemes.map((theme, themeIdx) => {
              const existingCount = theme.items.filter((i) => !i._isNew).length;
              const newCount = theme.items.filter((i) => i._isNew).length;
              return (
                <Accordion
                  key={themeIdx}
                  defaultExpanded
                  sx={{
                    mb: 1,
                    borderRadius: '12px !important',
                    '&:before': { display: 'none' },
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flex: 1,
                        mr: 1,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <TextField
                        size="small"
                        value={theme.name}
                        onChange={(e) =>
                          handleSafeEditThemeName(themeIdx, e.target.value)
                        }
                        label={language === 'en' ? 'Theme name' : 'Тема'}
                        sx={{ minWidth: 200 }}
                      />
                      {existingCount > 0 && (
                        <Chip
                          icon={
                            <LockIcon sx={{ fontSize: '0.9rem !important' }} />
                          }
                          label={`${existingCount} ${language === 'en' ? 'existing' : 'сущ.'}`}
                          size="small"
                          variant="outlined"
                          color="default"
                        />
                      )}
                      {newCount > 0 && (
                        <Chip
                          label={`+${newCount} ${language === 'en' ? 'new' : 'новых'}`}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                      <Tooltip
                        title={
                          theme._isNew
                            ? language === 'en'
                              ? 'Remove this theme'
                              : 'Удалить тему'
                            : language === 'en'
                              ? 'Delete theme and remove all its scores from student profiles'
                              : 'Удалить тему и все её оценки из профилей учеников'
                        }
                      >
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSafeDeleteTheme(themeIdx);
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <List dense disablePadding>
                      {theme.items.map((item, itemIdx) => (
                        <ListItem
                          key={itemIdx}
                          disableGutters
                          sx={{ gap: 1, py: 0.4 }}
                        >
                          <TextField
                            size="small"
                            fullWidth
                            value={item.text}
                            onChange={(e) =>
                              handleSafeEditItemText(
                                themeIdx,
                                itemIdx,
                                e.target.value
                              )
                            }
                            InputProps={{
                              sx: { borderRadius: 2 },
                            }}
                          />
                          <TextField
                            size="small"
                            value={item.meaning || ''}
                            onChange={(e) =>
                              handleSafeEditItemMeaning(
                                themeIdx,
                                itemIdx,
                                e.target.value
                              )
                            }
                            placeholder={
                              language === 'en'
                                ? 'animal / food, optional'
                                : 'животное / еда, необязательно'
                            }
                            InputProps={{ sx: { borderRadius: 2 } }}
                            sx={{ width: 180, flexShrink: 0 }}
                          />
                          <Chip
                            label={item.type === 'phrase' ? 'P' : 'W'}
                            size="small"
                            color={
                              item.type === 'phrase' ? 'secondary' : 'primary'
                            }
                            variant="outlined"
                            sx={{ minWidth: 32 }}
                          />
                          {item._isNew ? (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                handleSafeEditRemoveItem(themeIdx, itemIdx)
                              }
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          ) : (
                            <Tooltip
                              title={
                                language === 'en'
                                  ? 'Protected — already used in student assessments'
                                  : 'Защищено — уже используется в оценках'
                              }
                            >
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                <LockIcon
                                  sx={{
                                    fontSize: '0.95rem',
                                    color: 'text.disabled',
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          )}
                        </ListItem>
                      ))}
                    </List>
                    <SafeAddItemRow
                      themeIdx={themeIdx}
                      onAdd={handleSafeEditAddItem}
                      language={language}
                    />
                  </AccordionDetails>
                </Accordion>
              );
            })}

            {/* Add new theme */}
            <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                fullWidth
                placeholder={
                  language === 'en' ? 'New theme name…' : 'Название новой темы…'
                }
                value={newSafeThemeName}
                onChange={(e) => setNewSafeThemeName(e.target.value)}
                onKeyDown={(e) =>
                  e.key === 'Enter' &&
                  (e.preventDefault(), handleSafeEditAddTheme())
                }
              />
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={handleSafeEditAddTheme}
                disabled={!newSafeThemeName.trim()}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {language === 'en' ? 'Add Theme' : 'Добавить тему'}
              </Button>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setSafeEditOpen(false)}>
              {language === 'en' ? 'Cancel' : 'Отмена'}
            </Button>
            <Button
              variant="contained"
              onClick={handleSafeEditSave}
              disabled={safeEditSaving}
              startIcon={
                safeEditSaving ? (
                  <CircularProgress size={16} />
                ) : (
                  <EditItemsIcon />
                )
              }
            >
              {language === 'en' ? 'Save Changes' : 'Сохранить'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Preview Dialog */}
        <Dialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            {language === 'en' ? 'Preview' : 'Предпросмотр'}:{' '}
            {previewTemplate?.name}
          </DialogTitle>
          <DialogContent>
            {previewTemplate?.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {previewTemplate.description}
              </Typography>
            )}
            {previewTemplate?.themes.map((theme, idx) => (
              <Box key={idx} sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {theme.name || theme.themeName}
                </Typography>
                {theme.description && (
                  <Typography variant="body2" color="text.secondary">
                    {theme.description}
                  </Typography>
                )}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(theme.items || []).map((item, i) => (
                    <Chip
                      key={i}
                      label={item.text || item}
                      size="small"
                      color={item.type === 'phrase' ? 'secondary' : 'primary'}
                      variant="outlined"
                    />
                  ))}
                  {/* Backward compat for old templates */}
                  {theme.words &&
                    theme.words.map((w, i) => (
                      <Chip
                        key={`w${i}`}
                        label={typeof w === 'string' ? w : w.text}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  {theme.phrases &&
                    theme.phrases.map((p, i) => (
                      <Chip
                        key={`p${i}`}
                        label={typeof p === 'string' ? p : p.text}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    ))}
                </Box>
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewOpen(false)}>
              {language === 'en' ? 'Close' : 'Закрыть'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
}
